import { CheckCircle2, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import type { ShellContext } from '@/components/layout/AppShell';
import { LabelChip } from '@/components/tasks/LabelChip';
import { TaskGroup, TaskList } from '@/components/tasks/TaskList';
import { Button } from '@/components/ui/button';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuTrigger,
} from '@/components/ui/menu';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { useCompleted } from '@/hooks/useTasks';
import type { Label, TaskSummary } from '@/lib/api/types';
import { formatDayLong, toDayString } from '@/lib/dates';
import { getIntlLocale, useT, type MessageKey } from '@/lib/i18n';
import { PRIORITY_LABELS } from '@/lib/priority';
import { Page } from '@/routes/Today';
import { useTaskActions } from '@/routes/useTaskActions';

const PAGE_SIZE = 50;

type SortKey = 'completed_at' | 'priority' | 'title';
type GroupKey = 'day' | 'label' | 'priority' | 'none';

const SORT_LABELS: Record<SortKey, MessageKey> = {
  completed_at: 'completed.sort.completedAt',
  priority: 'completed.sort.priority',
  title: 'completed.sort.title',
};

const GROUP_LABELS: Record<GroupKey, MessageKey> = {
  day: 'completed.group.day',
  label: 'completed.group.label',
  priority: 'completed.group.priority',
  none: 'completed.group.none',
};

/** Descending urgency, matching the backend's PRIORITY_RANK. */
const PRIORITY_RANK: Record<TaskSummary['priority'], number> = {
  URGENT: 0,
  HIGH: 1,
  NORMAL: 2,
  LOW: 3,
  NONE: 4,
};

/**
 * Completed work.
 *
 * Sorting and grouping happen on the client, over the page already fetched.
 * That is honest for a page of fifty: pushing them to the server would mean a
 * round trip per control and a lot of query surface for a screen people mostly
 * scan. If Completed ever needs to be grouped across *all* history rather than
 * the current page, that is the point to move it server-side.
 */
/** Matches COMPLETED_RECENT_DAYS on the server; shown so the list says what it is. */
const RECENT_DAYS = 3;

export function CompletedRoute() {
  const t = useT();
  const { currentUser, openTask } = useOutletContext<ShellContext>();
  const [offset, setOffset] = useState(0);
  const [term, setTerm] = useState('');
  const [sort, setSort] = useState<SortKey>('completed_at');
  const [group, setGroup] = useState<GroupKey>('day');
  const { data, isPending, error, refetch } = useCompleted(offset, term);
  const searching = term.trim().length >= 3;
  const actions = useTaskActions(openTask);
  const today = toDayString(new Date());

  const groups = useMemo(
    () => (data ? buildGroups(data.items, sort, group, t) : []),
    [data, sort, group, t],
  );

  if (isPending)
    return (
      <Page>
        <LoadingState />
      </Page>
    );
  if (error)
    return (
      <Page>
        <ErrorState error={error} onRetry={() => void refetch()} />
      </Page>
    );
  if (!data) return null;

  return (
    <Page>
      <header className="mb-5 flex flex-wrap items-center gap-2">
        <h1 className="text-xl font-semibold tracking-tight">{t('nav.completed')}</h1>
        {data.meta.total > 0 ? (
          <span className="text-sm tabular-nums text-content-subtle">
            {data.meta.total}
          </span>
        ) : null}

        <div className="flex-1" />

        {/* The list covers the last few days; a question about March is asked
            here instead, and then the window does not apply. */}
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-content-subtle"
            aria-hidden
          />
          <Input
            value={term}
            onChange={(event) => {
              setTerm(event.target.value);
              // A new question starts at its first page, not the third one of
              // the answer before it.
              setOffset(0);
            }}
            placeholder={t('completed.search')}
            aria-label={t('completed.search')}
            className="w-56 pl-8"
          />
        </div>

        <Picker
          label={t('completed.sortBy')}
          value={t(SORT_LABELS[sort])}
          options={SORT_LABELS}
          onSelect={(key) => setSort(key as SortKey)}
        />
        <Picker
          label={t('completed.groupBy')}
          value={t(GROUP_LABELS[group])}
          options={GROUP_LABELS}
          onSelect={(key) => setGroup(key as GroupKey)}
        />
      </header>

      {/* Which of the two things this list currently is. Without it, an empty
          recent window looks exactly like a search that found nothing. */}
      <p className="mb-3 text-xs text-content-subtle">
        {searching
          ? t('completed.searchingAll')
          : t('completed.recentOnly', { days: String(RECENT_DAYS) })}
      </p>

      {data.items.length === 0 ? (
        <EmptyState
          icon={<CheckCircle2 className="h-5 w-5" />}
          title={searching ? t('common.noResults') : t('completed.empty.title')}
          hint={searching ? t('completed.search.hint') : t('completed.empty.hint')}
        />
      ) : (
        groups.map((entry) => (
          <TaskGroup key={entry.key} title={entry.title} count={entry.tasks.length}>
            {entry.chip ? (
              <div className="mb-1.5 -mt-1">
                <LabelChip label={entry.chip} />
              </div>
            ) : null}
            <TaskList
              tasks={entry.tasks}
              today={today}
              currentUserId={currentUser.id}
              actions={actions}
              showDate={group !== 'day'}
            />
          </TaskGroup>
        ))
      )}

      {data.meta.total > PAGE_SIZE ? (
        <nav
          className="mt-6 flex items-center gap-2"
          aria-label={t('common.pagination')}
        >
          <Button
            variant="secondary"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            {t('common.previous')}
          </Button>
          <span className="text-xs tabular-nums text-content-subtle">
            {offset + 1}–{Math.min(offset + PAGE_SIZE, data.meta.total)} z{' '}
            {data.meta.total}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={!data.meta.has_more}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            {t('common.next')}
          </Button>
        </nav>
      ) : null}
    </Page>
  );
}

function Picker({
  label,
  value,
  options,
  onSelect,
}: {
  label: string;
  value: string;
  /** Option key → message key; the menu translates as it renders. */
  options: Record<string, MessageKey>;
  onSelect: (key: string) => void;
}) {
  const t = useT();
  return (
    <Menu>
      <MenuTrigger asChild>
        <Button variant="secondary" size="sm" className="gap-1.5">
          <span className="text-content-subtle">{label}:</span>
          {value}
        </Button>
      </MenuTrigger>
      <MenuContent align="end">
        <MenuLabel>{label}</MenuLabel>
        {Object.entries(options).map(([key, message]) => (
          <MenuItem
            key={key}
            onSelect={() => onSelect(key)}
            className={value === t(message) ? 'bg-surface-hover' : ''}
          >
            {t(message)}
          </MenuItem>
        ))}
      </MenuContent>
    </Menu>
  );
}

interface Group {
  key: string;
  title: string;
  tasks: TaskSummary[];
  chip?: Label;
}

function sortTasks(tasks: TaskSummary[], sort: SortKey): TaskSummary[] {
  const sorted = [...tasks];
  if (sort === 'completed_at') {
    // Most recently finished first — the useful end of a history.
    sorted.sort((a, b) => (b.completed_at ?? '').localeCompare(a.completed_at ?? ''));
  } else if (sort === 'priority') {
    sorted.sort(
      (a, b) =>
        PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority] ||
        (b.completed_at ?? '').localeCompare(a.completed_at ?? ''),
    );
  } else {
    sorted.sort((a, b) => a.title.localeCompare(b.title, getIntlLocale()));
  }
  return sorted;
}

function buildGroups(
  items: TaskSummary[],
  sort: SortKey,
  group: GroupKey,
  t: (key: MessageKey) => string,
): Group[] {
  const sorted = sortTasks(items, sort);

  if (group === 'none') {
    return [{ key: 'all', title: t('common.all'), tasks: sorted }];
  }

  if (group === 'priority') {
    const buckets = new Map<string, TaskSummary[]>();
    for (const task of sorted) {
      const bucket = buckets.get(task.priority) ?? [];
      bucket.push(task);
      buckets.set(task.priority, bucket);
    }
    return [...buckets.entries()]
      .sort(
        ([a], [b]) =>
          PRIORITY_RANK[a as TaskSummary['priority']] -
          PRIORITY_RANK[b as TaskSummary['priority']],
      )
      .map(([priority, tasks]) => ({
        key: priority,
        title: t(PRIORITY_LABELS[priority as TaskSummary['priority']]),
        tasks,
      }));
  }

  if (group === 'label') {
    const buckets = new Map<string, { label: Label; tasks: TaskSummary[] }>();
    const untagged: TaskSummary[] = [];

    for (const task of sorted) {
      if (task.labels.length === 0) {
        untagged.push(task);
        continue;
      }
      // A task with several labels appears under each — that is what grouping
      // by label means, and hiding it from all but the first would lose it.
      for (const label of task.labels) {
        const bucket = buckets.get(label.id) ?? { label, tasks: [] };
        bucket.tasks.push(task);
        buckets.set(label.id, bucket);
      }
    }

    const groups: Group[] = [...buckets.values()]
      .sort((a, b) => a.label.sort_order - b.label.sort_order)
      .map(({ label, tasks }) => ({
        key: label.id,
        title: label.name,
        tasks,
        chip: label,
      }));

    if (untagged.length > 0) {
      groups.push({ key: 'untagged', title: t('completed.noLabel'), tasks: untagged });
    }
    return groups;
  }

  // group === 'day'
  const buckets = new Map<string, TaskSummary[]>();
  for (const task of sorted) {
    const day = task.completed_at ? task.completed_at.slice(0, 10) : 'unknown';
    const bucket = buckets.get(day) ?? [];
    bucket.push(task);
    buckets.set(day, bucket);
  }
  return [...buckets.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([day, tasks]) => ({
      key: day,
      title: day === 'unknown' ? 'Bez data' : formatDayLong(day),
      tasks,
    }));
}
